import axios from 'axios';
import { addCsrfTokenToConfig } from '../../utils/csrfUtils';

/**
 * API请求工具函数
 */

// API 基礎 URL
const API_URL = '/api';

// 獲取 token 的輔助函數
const getToken = () => {
    return localStorage.getItem('token');
};

// 创建axios实例
const api = axios.create({
    baseURL: API_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// 添加请求拦截器
api.interceptors.request.use(
    (config) => {
        // 从localStorage获取token
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * 客户相关API
 */

/**
 * 获取所有客户
 * @param {string} token - 认证令牌
 * @returns {Promise} 客户列表
 */
export const fetchCustomers = async (token) => {
    try {
        const response = await api.get('/customers', {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        console.error('获取客户列表失败:', error);
        throw error;
    }
};

/**
 * 获取客户详情
 * @param {string} customerId - 客户ID
 * @param {string} token - 认证令牌
 * @returns {Promise} 客户详情
 */
export const fetchCustomerById = async (customerId, token) => {
    // console.log('fetchCustomerById 被調用，客戶ID:', customerId);

    if (!customerId) {
        console.error('客戶ID為空，無法獲取客戶');
        throw new Error('客戶ID為空');
    }

    if (!token) {
        console.error('認證令牌為空，無法獲取客戶');
        throw new Error('認證令牌為空');
    }

    try {
        // 嘗試直接從 API 獲取客戶詳情
        try {
            // console.log('嘗試直接從API獲取客戶詳情');
            const directResponse = await api.get(`/customers/${customerId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (directResponse.data && directResponse.data.id) {
                // console.log('直接從 API 獲取客戶詳情成功:', directResponse.data);
                return directResponse.data;
            }
        } catch (directError) {
            // console.error('直接獲取客戶詳情失敗，嘗試從客戶列表中查找:', directError);
        }

        // 如果直接獲取失敗，則從所有客戶中獲取特定客戶
        // console.log('嘗試從客戶列表中查找客戶');
        const response = await api.get('/customers', {
            headers: { Authorization: `Bearer ${token}` }
        });

        // 找到匹配的客戶
        const foundCustomer = response.data.find(c =>
            c.id.split('/').pop() === customerId || c.id === customerId
        );

        if (!foundCustomer) {
            // console.error('在客戶列表中找不到客戶資料');
            throw new Error('找不到客戶資料');
        }

        // console.log('在客戶列表中找到客戶:', foundCustomer);
        return foundCustomer;
    } catch (error) {
        // console.error('獲取客戶詳情失敗:', error);
        throw error;
    }
};

/**
 * 保修单相关API
 */

/**
 * 获取所有保修单
 * @param {string} token - 认证令牌
 * @returns {Promise} 保修单列表
 */
export const fetchAllWarranties = async (token) => {
    try {
        const response = await api.get('/customers/warranties', {
            headers: { Authorization: `Bearer ${token}` }
        });

        // 处理不同的响应格式
        let extractedWarranties = [];

        if (Array.isArray(response.data)) {
            extractedWarranties = response.data;
        } else if (response.data && response.data.warranties && Array.isArray(response.data.warranties)) {
            extractedWarranties = response.data.warranties;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
            extractedWarranties = response.data.data;
        } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
            extractedWarranties = response.data.results;
        }

        return extractedWarranties;
    } catch (error) {
        console.error('获取保修单列表失败:', error);
        throw error;
    }
};

/**
 * 更新保養單狀態
 * @param {string|number} warrantyId - 保養單ID
 * @param {Object} updateData - 更新數據
 * @param {string} updateData.status - 新狀態
 * @param {string} updateData.notes - 備註
 * @param {string} token - 認證令牌
 * @returns {Promise<boolean>} - 成功返回 true，失敗拋出錯誤
 */
export const updateWarrantyStatus = async (warrantyId, updateData, token) => {
    // console.log(`開始更新保養單狀態: ID=${warrantyId}, 數據=`, updateData);

    // 如果 warrantyId 是 "warranty-123" 格式，提取數字部分
    let id = warrantyId;
    if (typeof warrantyId === 'string' && warrantyId.includes('-')) {
        id = warrantyId.split('-')[1];
    }

    // 從 Shopify Metaobject ID 中提取純數字 ID
    if (typeof warrantyId === 'string' && warrantyId.startsWith('gid://shopify/Metaobject/')) {
        id = warrantyId.split('/').pop();
        // console.log(`從 Shopify Metaobject ID 中提取純數字 ID: ${id}`);
    }

    try {
        // 準備請求配置
        const config = {
            method: 'PUT',
            url: `${API_URL}/customers/warranty/update?id=${id}`,
            data: {
                verification_status: updateData.status,
                verification_notes: updateData.notes || '',
                warranty_expiration_date: updateData.warranty_expiration_date || null,
                verification_date: updateData.verification_date || null
            },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token || getToken()}`
            }
        };

        // 添加 CSRF 令牌
        const configWithCsrf = addCsrfTokenToConfig(config);

        // 發送請求
        const response = await axios(configWithCsrf);

        // console.log('保養單狀態更新成功:', response.data);

        // 檢查響應是否成功
        if (response.status >= 200 && response.status < 300) {
            return true;
        } else {
            throw new Error(`保養單更新失敗: ${response.status} ${response.statusText}`);
        }
    } catch (error) {
        console.error('保養單狀態更新過程中發生錯誤:', error);
        throw error; // 失敗時拋出錯誤
    }
};

/**
 * 获取客户的保修单
 * @param {string} customerId - 客户ID
 * @param {string} token - 认证令牌
 * @returns {Promise} 保修单列表
 */
export const fetchCustomerWarranties = async (customerId, token) => {
    try {
        const response = await api.get(`/customers/${customerId}/warranties`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        console.error('获取客户保修单失败:', error);
        throw error;
    }
}; 